import { TrackerActivationType } from '@racemap/sdk/schema/trackers';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import { moveArrayEntry } from '@racemap/utilities/math';
import type { RacemapStarter } from '@racemap/utilities/types/events';
import { type Immutable, produce } from 'immer';
import chunk from 'lodash/chunk';
import { toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import type { DraftState, State } from '../../reducers';
import type { CurrentEvent, EventsActions } from '../events_reducers';
import { parseStartersCSVFile, validateRawStarters } from '../starters_csv';
import {
  type GetState,
  logAndToast,
  patchCurrentEvent,
  type SetState,
  withSavingState,
} from './helpers';

const apiClient = RacemapAPIClient.fromWindowLocation();
// Per-starter updates need one request each; cap how many are in flight at once.
const STARTER_UPDATE_BATCH_SIZE = 10;

type StarterPatch = { id: string; patch: Partial<RacemapStarter> };

// Apply the patches optimistically so inline edits (e.g. clearing a tag) reflect
// immediately, instead of briefly re-rendering the stale value until the server
// round-trip resolves.
const applyStarterPatches = (
  set: SetState,
  eventId: string | undefined,
  patches: Array<StarterPatch>,
) =>
  set(
    produce((s: DraftState) => {
      if (eventId == null) return;
      const event = s.events.items.get(eventId);
      if (event?.starters == null) return;

      for (const { id, patch } of patches) {
        const existing = event.starters.get(id);
        if (existing != null) event.starters.set(id, { ...existing, ...patch });
      }
    }),
  );

// Writes the starters the server returned, then reloads the starters of the current event.
async function storeUpdatedStarters(
  set: SetState,
  get: GetState,
  currentEvent: Immutable<CurrentEvent> | null,
  ids: Array<string>,
  newStarters: Array<RacemapStarter>,
) {
  if (currentEvent == null || !currentEvent.starters?.has(ids[0])) return;

  set(
    produce((s) => {
      const event = s.events.items.get(currentEvent.id);
      for (const starter of newStarters) {
        event.starters.set(starter.id, {
          ...(event.starters.get(starter.id) || {}),
          ...starter,
        });
      }
    }),
  );
  await get().events.actions.loadCurrentStarters(currentEvent.id);
}

// Reverts optimistic patches by reloading the authoritative state from the server.
async function reloadStartersAfterFailure(get: GetState, eventId: string | undefined) {
  if (eventId == null) return;
  try {
    await get().events.actions.loadCurrentStarters(eventId);
  } catch {
    // Keep the original update error.
  }
}

function reportStarterUpdateError(error: unknown, subject: 'starters' | 'tags') {
  if (!(error instanceof Error)) return;
  if (error instanceof HTTPFetchError) {
    console.error(`Failed to update ${subject}: ${error.message}`);
    if (error.serverError != null) console.error(error.serverError);
    toast.error(
      `The attempt to update the ${subject} failed.${error.serverError ? ` ${error.serverError}` : ''}`,
    );
    return;
  }
  console.error(error);
  toast.error(`The attempt to update the ${subject} failed. Error: UNKNOWN`);
}

// Reloads the starters of the current event after they were added or removed.
async function refreshStartersOfCurrentEvent(set: SetState, get: GetState, eventId: string) {
  if (get().events.currentEventId !== eventId) return;

  const starters = await apiClient.getEventStarters(eventId, { noFilter: true });
  patchCurrentEvent(set, get, eventId, () => ({
    starters: new Map(starters.map((starter) => [starter.id, starter])),
  }));
}

export const createStarterActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<
  EventsActions,
  | 'addStarters'
  | 'importStartersCSVFile'
  | 'changeStarterPositions'
  | 'updateStarters'
  | 'updateStartersIndividually'
  | 'setTagsOnStarters'
  | 'deleteStarters'
  | 'dearchiveTracker'
  | 'generateStarterKeys'
> => ({
  addStarters: (eventId, newStarters) =>
    withSavingState(
      set,
      async () => {
        await apiClient.createStarters(newStarters, eventId);
        await refreshStartersOfCurrentEvent(set, get, eventId);
      },
      (error) => {
        console.log(`Failed to add a starter: ${error}`);
        const names = newStarters.map((s) => s.name).join('/');
        toast.error(
          error instanceof HTTPFetchError
            ? `The attempt to add the starter ${names} failed. ${error.serverError}`
            : `The attempt to add the starter ${names} failed.`,
        );
      },
    ),
  importStartersCSVFile: (eventId, file) =>
    withSavingState(
      set,
      async () => {
        const rawStarters = await parseStartersCSVFile(file);
        validateRawStarters(rawStarters);
        await get().events.actions.addStarters(eventId, rawStarters);
      },
      (error) => {
        console.log('Failed to add a starter');
        console.error(error);
        toast.error(`The attempt to import the starters failed: ${error}`);
      },
    ),
  changeStarterPositions: (eventId, moves) =>
    withSavingState(
      set,
      async () => {
        const events = get().events;
        const event = events.items.get(eventId);
        if (event == null) throw new Error(`Event with id ${eventId} not found!`);

        const starterIds =
          event.starters != null
            ? Array.from(event.starters.values()).map((s) => s.id)
            : (await apiClient.getEventStarters(eventId, { noFilter: true })).map((s) => s.id);

        let newStarterIds = starterIds;
        for (const move of moves) {
          newStarterIds = moveArrayEntry(newStarterIds, move.currentPosition, move.newPosition);
        }

        await events.actions.updateEvent(eventId, [{ key: 'starterIds', newValue: newStarterIds }]);
      },
      logAndToast('Failed to move a starter', 'The attempt to move the starter failed.'),
    ),
  updateStarters: async (ids, starterUpdate) => {
    const currentEvent = get().events.getter.currentEvent();
    await withSavingState(
      set,
      async () => {
        applyStarterPatches(
          set,
          currentEvent?.id,
          ids.map((id) => ({ id, patch: starterUpdate })),
        );
        const newStarters = await apiClient.updateStarters(ids, starterUpdate);
        await storeUpdatedStarters(set, get, currentEvent, ids, newStarters);
      },
      async (error) => {
        await reloadStartersAfterFailure(get, currentEvent?.id);
        reportStarterUpdateError(error, 'starters');
      },
    );
  },
  // Like updateStarters, but every starter gets its own patch (e.g. one color each).
  // The API only accepts one patch per request, so the requests are sent in bounded
  // batches and the authoritative state is reloaded once at the end instead of per starter.
  updateStartersIndividually: async (patches) => {
    const currentEvent = get().events.getter.currentEvent();
    await withSavingState(
      set,
      async () => {
        applyStarterPatches(set, currentEvent?.id, patches);
        for (const batch of chunk(patches, STARTER_UPDATE_BATCH_SIZE)) {
          await Promise.all(batch.map(({ id, patch }) => apiClient.updateStarters([id], patch)));
        }

        if (currentEvent != null) {
          await get().events.actions.loadCurrentStarters(currentEvent.id);
        }
      },
      async (error) => {
        await reloadStartersAfterFailure(get, currentEvent?.id);
        reportStarterUpdateError(error, 'starters');
      },
    );
  },
  // ponytail: mirrors updateStarters; only the API call differs (dot-path tag merge).
  setTagsOnStarters: (ids, tagPatch) =>
    withSavingState(
      set,
      async () => {
        const newStarters = await apiClient.setTagsOnStarters(ids, tagPatch);
        await storeUpdatedStarters(set, get, get().events.getter.currentEvent(), ids, newStarters);
      },
      (error) => reportStarterUpdateError(error, 'tags'),
    ),
  deleteStarters: (eventId: string, starterIds: Array<string>) =>
    withSavingState(
      set,
      async () => {
        await apiClient.removeStarters(starterIds, eventId);
        await refreshStartersOfCurrentEvent(set, get, eventId);
      },
      logAndToast('Failed to delete a starter', 'The attempt to delete the starter failed.'),
    ),
  dearchiveTracker: async (trackerId) => {
    try {
      await apiClient.updateTracker(trackerId, {
        'activation.type': TrackerActivationType.ACTIVATED,
      });
      const currentEvent = get().events.getter.currentEvent();
      if (currentEvent == null) return;

      const starterOfDevice = Array.from(currentEvent.starters?.values() || []).find(
        (s) => s.deviceId === trackerId,
      );
      if (starterOfDevice != null) {
        set(
          produce((s: DraftState) => {
            const event = s.events.items.get(currentEvent.id);
            const starter = event?.starters?.get(starterOfDevice.id);
            if (starter?.deviceActivation != null) {
              starter.deviceActivation.type = TrackerActivationType.ACTIVATED;
            }
          }),
        );
      }

      toast.success('Tracker dearchived successfully.');
      get().events.actions.loadCurrentEvent(currentEvent.id);
    } catch (error) {
      console.error(`Failed to dearchive tracker: ${error}`);
      toast.error(`The attempt to dearchive the tracker ${trackerId} failed.`);
    }
  },
  generateStarterKeys: (eventId, count) =>
    withSavingState(
      set,
      async () => {
        const keys = await apiClient.createEventStarterKeys(eventId, count);
        if (get().events.currentEventId !== eventId) return;

        const keysMap = new Map(keys.map((key) => [key.key, key]));
        // append new keys to existing keys
        patchCurrentEvent(set, get, eventId, (event) => ({
          keys: new Map([...(event?.keys ?? []), ...keysMap]),
        }));
      },
      logAndToast('Failed to create keys for starter', 'Failed to create keys for starter.'),
    ),
});
